const maxReportsPerPage = 20;
const maxEmailStackFrames = 30;
const defaultFrontendLoggingEmail = 'ua4work@gmail.com';

function getFrontendLoggingEmail() {
  if (!document.kvConfig) {
    return defaultFrontendLoggingEmail;
  }

  return document.kvConfig.frontendLoggingEmail || defaultFrontendLoggingEmail;
}

function buildEmailLogContext(payload) {
  return {
    app: payload.app,
    userId: payload.userId,
    jobId: payload.kvConfig && payload.kvConfig.jobId,
    visualizerType: payload.visualizerType,
    navigationType: payload.navigationType,
    toolVersion: payload.kvConfig && payload.kvConfig.toolVersion,
    isBackend: payload.kvConfig && payload.kvConfig.isBackend,
    release: payload.release,
    url: payload.url,
    browser: payload.browser,
    timestamp: payload.timestamp,
  };
}

function parseStackFrame(frame) {
  const chromeFrameWithFunction = frame.match(/^at\s+(.+?)\s+\((.+):(\d+):(\d+)\)$/);

  if (chromeFrameWithFunction) {
    return {
      raw: frame,
      functionName: chromeFrameWithFunction[1],
      file: chromeFrameWithFunction[2],
      line: Number(chromeFrameWithFunction[3]),
      column: Number(chromeFrameWithFunction[4]),
    };
  }

  const chromeFrameWithoutFunction = frame.match(/^at\s+(.+):(\d+):(\d+)$/);

  if (chromeFrameWithoutFunction) {
    return {
      raw: frame,
      functionName: null,
      file: chromeFrameWithoutFunction[1],
      line: Number(chromeFrameWithoutFunction[2]),
      column: Number(chromeFrameWithoutFunction[3]),
    };
  }

  const firefoxFrame = frame.match(/^(.*?)@(.+):(\d+):(\d+)$/);

  if (firefoxFrame) {
    return {
      raw: frame,
      functionName: firefoxFrame[1] || null,
      file: firefoxFrame[2],
      line: Number(firefoxFrame[3]),
      column: Number(firefoxFrame[4]),
    };
  }

  return {
    raw: frame,
  };
}

function splitStackFrames(stack, dropErrorHeading = false) {
  if (!stack) {
    return [];
  }

  const frames = String(stack)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (dropErrorHeading && frames.length && frames[0].indexOf('at ') !== 0 && frames[0].indexOf('@') === -1) {
    return frames.slice(1).map(parseStackFrame);
  }

  return frames.map(parseStackFrame);
}

function buildEmailLogStackTrace(payload) {
  const context = buildEmailLogContext(payload);
  const allStackFrames = splitStackFrames(payload.stack || payload.stackPreview, true);
  const allComponentFrames = splitStackFrames(payload.componentStack);
  const stackFrames = allStackFrames.slice(0, maxEmailStackFrames);
  const componentFrames = allComponentFrames.slice(0, maxEmailStackFrames);
  const stackTrace = {
    error: {
      type: payload.type,
      level: payload.level,
      name: payload.name,
      message: payload.message || 'Unknown frontend error',
      source: payload.source,
      line: payload.line,
      column: payload.column,
    },
    topFrame: stackFrames[0] || null,
    stackFrames,
    componentFrames,
    framesTruncated: {
      stackFrames: allStackFrames.length > stackFrames.length,
      componentFrames: allComponentFrames.length > componentFrames.length,
    },
    context,
  };

  return JSON.stringify(stackTrace, null, 2);
}

export function buildEmailLogPayload(payload) {
  const body = {
    message: payload.summary || payload.message || 'Unknown frontend error',
    stackTrace: buildEmailLogStackTrace(payload),
  };
  const email = getFrontendLoggingEmail();

  if (email) {
    body.email = email;
  }

  return body;
}

export function createErrorTransport(endpointUrl, options = {}) {
  const fetchBeforeInstrumentation = window.fetch;
  let reportsSent = 0;

  function sendWithFetch(body) {
    const fetchFn = fetchBeforeInstrumentation || window.fetch;

    if (!fetchFn) {
      return false;
    }

    fetchFn.call(window, endpointUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
      keepalive: true,
    }).catch(() => {});

    return true;
  }

  function sendWithXhr(body) {
    const request = new XMLHttpRequest();

    request.open('POST', endpointUrl, true);
    request.setRequestHeader('Content-Type', 'application/json');
    request.send(body);
  }

  return {
    send(payload) {
      if (reportsSent >= maxReportsPerPage) {
        return;
      }

      reportsSent += 1;
      const payloadBody = options.formatPayload ? options.formatPayload(payload) : payload;
      const body = JSON.stringify(payloadBody);

      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        const sent = navigator.sendBeacon(endpointUrl, blob);

        if (sent) {
          return;
        }
      }

      if (sendWithFetch(body)) {
        return;
      }

      sendWithXhr(body);
    },
  };
}
